import React, { useEffect, useRef, useState } from 'react';

interface SimpleRevealProps {
  children: React.ReactNode;
  delay?: number;
  className?: string;
  onVisible?: () => void;
}

export function SimpleReveal({ 
  children, 
  delay = 0, 
  className = '',
  onVisible
}: SimpleRevealProps) {
  const elementRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Show immediately if IntersectionObserver isn't supported
    if (typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          const target = entry.target as Element;
          setTimeout(() => {
            setIsVisible(true);
            onVisible?.();
            // Stop observing once revealed
            observer.unobserve(target);
          }, delay);
        }
      },
      { threshold: 0.1, rootMargin: '-50px' }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [delay, onVisible]);

  return (
    <div
      ref={elementRef}
      className={`reveal ${isVisible ? 'in-view' : ''} ${className}`}
    >
      {children}
    </div>
  );
}

interface RevealGroupProps {
  children: React.ReactNode;
  stagger?: number;
  className?: string;
}

export function RevealGroup({ children, stagger = 80, className = '' }: RevealGroupProps) {
  return (
    <div className={className}>
      {React.Children.map(children, (child, index) => (
        <SimpleReveal delay={index * stagger}>
          {child}
        </SimpleReveal>
      ))}
    </div>
  );
}